import { Alert as MUIAlert, AlertTitle, styled } from '@mui/material';

import { PropsWithChildren, ReactNode, useState } from 'react';

import {
  ErrorCircleIcon,
  ErrorIcon,
  InfoCircleIcon,
  InfoIcon,
  SuccessIcon,
  UnfoldLessIcon,
  UnfoldMoreIcon,
  WarningCircleIcon,
  WarningIcon,
} from '@app-velocity/icons';

import { ActionButton } from './components/ActionButton';

export type AlertProps = PropsWithChildren<{
  severity: 'info' | 'error' | 'success' | 'warning';
  size?: 'compact';
  collapsable?: boolean;
  title?: string;
  rightAction?: ReactNode;
  message?: ReactNode;
  customIcon?: ReactNode;
  onClose?: () => void;
  onClick?: () => void;
}>;

const StyledMessage = styled('p')<{ size?: 'compact' }>(({ size }) => ({
  margin: 0,
  whiteSpace: 'pre-wrap',
  width: '100%',
  fontSize: size === 'compact' ? 12 : 13,
}));

const StyledAlertTitle = styled(AlertTitle)({
  marginBottom: 0,
  margin: 0,
});

const StyledExpandButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: theme.spacing(1),
}));

export const Alert = ({
  severity,
  title,
  size,
  message,
  rightAction,
  children,
  collapsable,
  customIcon,
  onClose,
  onClick,
}: AlertProps) => {
  const [expanded, setExpanded] = useState(true);

  const toggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  const getAction = () => {
    if (rightAction) return rightAction;
    if (!collapsable) return null;

    return (
      <StyledExpandButton type="button" onClick={toggleExpanded}>
        {expanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
      </StyledExpandButton>
    );
  };

  const getIconForSeverity = (
    severity: 'info' | 'warning' | 'error' | 'success'
  ) => {
    if (customIcon) return customIcon;

    switch (severity) {
      case 'info':
        return size === 'compact' ? <InfoCircleIcon /> : <InfoIcon />;
      case 'warning':
        return size === 'compact' ? <WarningCircleIcon /> : <WarningIcon />;
      case 'error':
        return size === 'compact' ? <ErrorCircleIcon /> : <ErrorIcon />;
      case 'success':
        return <SuccessIcon />;
    }
  };

  return (
    <MUIAlert
      variant={size === 'compact' ? 'compact' : undefined}
      onClose={onClose}
      onClick={onClick}
      severity={severity}
      iconMapping={{
        info: getIconForSeverity('info'),
        warning: getIconForSeverity('warning'),
        error: getIconForSeverity('error'),
        success: getIconForSeverity('success'),
      }}
      action={getAction()}
      sx={{
        cursor: onClick ? 'pointer' : 'unset',
      }}
    >
      {title ? <StyledAlertTitle>{title}</StyledAlertTitle> : null}
      {expanded ? (
        <>
          {message ? (
            <StyledMessage size={size}>{message}</StyledMessage>
          ) : null}
          {children}
        </>
      ) : null}
    </MUIAlert>
  );
};

Alert.ActionButton = ActionButton;
